import { type ReactNode, type FC } from 'react';
import { Box, Text } from '@chakra-ui/react';
import SearchResults from './SearchResults';
import { WhatWentWrongButton } from './WhatWentWrongButton';
import { GlobalResultsButton } from './GlobalResultsButton';
import { Timestamp } from '../../../../services/Timestamp';
import { useTripsContext } from '../../../GlobalContextProvider/GlobalContextProvider';
import { translateAndFormat } from '../../../../constants/translation';
import { ReactComponent as RouteIcon } from '../../../../assets/svg/Route.svg';
import { type SearchContext, type TripSearchResultViewModel } from '../../types';
import { canChangeContextToGlobal, formatDateTime } from '../../utils/search-utils';

interface Props {
  jobAndTripsResults: TripSearchResultViewModel[];
  isLoading: boolean;
  onTripOrJobSelected: (operation: string, mmsi: string, timestamp: Timestamp, vesselName: string) => void;
  onSearchClose: () => void;
  onWhatWentWrongClick: () => void;
  searchPhrase: string;
  context: SearchContext;
  onGlobalResultsRequested: (searchPhrase: string) => void;
}

const JobsAndTripsSearchResults: FC<Props> = ({
  jobAndTripsResults,
  isLoading,
  onTripOrJobSelected,
  onSearchClose,
  onWhatWentWrongClick,
  searchPhrase,
  context,
  onGlobalResultsRequested,
}) => {
  const { operations } = useTripsContext();
  const selectTripOrJob = (tripOrJob: TripSearchResultViewModel): void => {
    const { operation, mmsi, timestamp, vesselName } = tripOrJob;
    onTripOrJobSelected(
      operation,
      mmsi?.toString(),
      Timestamp.fromMilliseconds(timestamp.valueInMilliseconds),
      vesselName
    );
    onSearchClose();
  };

  const tripOrJobNodeFactory = (tripOrJob: TripSearchResultViewModel, index: number): ReactNode => {
    const timezone = operations.find(o => o.key === tripOrJob.operation)?.timezone ?? 'UTC';
    return (
      <Box
        key={index}
        onClick={() => {
          selectTripOrJob(tripOrJob);
        }}
        className="result-item"
      >
        <RouteIcon className="result-icon" />
        <div className="trip-or-job-result">
          <div className="result-text trip-or-job-text">{tripOrJob.assistedVesselName}</div>
          <div className="result-subtext trip-or-job-subtext text-sm-regular">
            <div className="subtext-left">{formatDateTime(tripOrJob.timestamp, timezone)}</div>
            <div className="subtext-right">{tripOrJob.operation}</div>
          </div>
        </div>
      </Box>
    );
  };

  const showRedirectToGlobal = canChangeContextToGlobal(context);

  const noResultsMessage = (
    <div className="no-result-actions">
      <Text className="text-sm-regular jobs-and-trips-no-results">
        {translateAndFormat('NO_TRIPS_OR_JOBS_FOUND_FOR', searchPhrase)}
      </Text>
      <WhatWentWrongButton onWhatWentWrongClick={onWhatWentWrongClick} />
      {showRedirectToGlobal && (
        <GlobalResultsButton searchPhrase={searchPhrase} onGlobalResultsRequested={onGlobalResultsRequested} />
      )}
    </div>
  );

  return (
    <SearchResults<TripSearchResultViewModel>
      results={jobAndTripsResults}
      isLoading={isLoading}
      headerTranslationKey="JOBS_AND_TRIPS"
      resultNodeFactory={tripOrJobNodeFactory}
      noResultsMessage={noResultsMessage}
    />
  );
};

export default JobsAndTripsSearchResults;
